
// DPL DEW — Dcode Estimate Weaver — Brand Logo Components
// Updated for Design System v3 (Midnight Emerald)

interface LogoProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'full' | 'icon';
  theme?: 'light' | 'dark';
}

const sizes = {
  sm: { icon: 24, full: 120 },
  md: { icon: 32, full: 160 },
  lg: { icon: 48, full: 200 },
  xl: { icon: 64, full: 280 },
};

export function DPLDEWLogo({
  className = '',
  size = 'md',
  variant = 'full',
  theme = 'dark', // Default to dark for Midnight Emerald
}: LogoProps) {
  const iconSize = sizes[size].icon;
  const fullWidth = sizes[size].full;
  
  // Theme Overrides for Midnight Emerald (Emerald + Amber)
  const emerald = '#10B981'; 
  const amber   = '#F59E0B'; 
  const slate   = '#94A3B8';
  const obsidian = '#050505';

  if (variant === 'icon') {
    return (
      <svg width={iconSize} height={iconSize} viewBox="0 0 32 32" fill="none"
        xmlns="http://www.w3.org/2000/svg" className={className}>
        <rect width="32" height="32" rx="9" fill={emerald} />
        {/* Abstract "W" for Weaver */}
        <path d="M8 10L12 22L16 12L20 22L24 10" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="23" cy="9.5" r="3" fill={amber} />
      </svg>
    );
  }

  return (
    <svg width={fullWidth * 1.1} height={iconSize * 1.4} viewBox="0 0 220 52" fill="none"
      xmlns="http://www.w3.org/2000/svg" className={className}>
      <rect x="0" y="4" width="44" height="44" rx="11" fill={emerald} />
      <path d="M12 16L18 32L22 22L26 32L32 16" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="36" cy="15" r="4" fill={amber} />
      
      <text x="56" y="26" fontFamily="'Outfit', system-ui, sans-serif"
        fontWeight="900" fontSize="19" fill="white" letterSpacing="-0.5">DCODE</text>
      <text x="124" y="26" fontFamily="'Outfit', system-ui, sans-serif"
        fontWeight="900" fontSize="19" fill={emerald} letterSpacing="-0.5">DEW</text>
      
      <line x1="56" y1="32" x2="180" y2="32" stroke={amber} strokeWidth="1.5" strokeOpacity="0.4" />
      
      <text x="56" y="44" fontFamily="'Inter', system-ui, sans-serif"
        fontWeight="600" fontSize="8.5" fill={slate} letterSpacing="2.5">ESTIMATE WEAVER</text>
    </svg>
  );
}

export const DPLHomestarLogo = DPLDEWLogo;

export function DcodeLogo({
  className = '',
  size = 'md',
  theme = 'dark',
}: Omit<LogoProps, 'variant'>) {
  const iconSize = sizes[size].icon;
  const emerald = '#10B981';
  const white = '#FFFFFF';

  return (
    <svg width={iconSize * 2.5} height={iconSize} viewBox="0 0 100 40" fill="none"
      xmlns="http://www.w3.org/2000/svg" className={className}>
      <rect x="4" y="8" width="24" height="24" rx="6" fill={emerald} fillOpacity="0.1" stroke={emerald} strokeWidth="2" />
      <text x="16" y="25" textAnchor="middle" fontFamily="monospace" fontWeight="800" fontSize="12" fill={emerald}>{'<D>'}</text>
      <text x="38" y="26" fontFamily="'Outfit', system-ui, sans-serif" fontWeight="800" fontSize="14" fill={white} letterSpacing="1">CODE</text>
      <text x="38" y="36" fontFamily="'Inter', system-ui, sans-serif" fontWeight="500" fontSize="6.5" fill={white} opacity="0.4">Dcode Private Limited</text>
    </svg>
  );
}

export function BrandHeader({
  className = '',
  showDcode = true,
  theme = 'dark',
}: {
  className?: string;
  showDcode?: boolean;
  theme?: 'light' | 'dark';
}) {
  return (
    <div className={`flex items-center gap-4 ${className}`}>
      <DPLDEWLogo size="md" theme={theme} />
      {showDcode && (
        <>
          <div className={`w-px h-8 bg-muted/50`} />
          <DcodeLogo size="sm" theme={theme} />
        </>
      )}
    </div>
  );
}

export function DPLIcon({
  className = '',
  size = 32,
}: {
  className?: string;
  size?: number;
}) {
  const emerald = 'hsl(158 94% 35%)';
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none"
      xmlns="http://www.w3.org/2000/svg" className={className}>
      <rect width="32" height="32" rx="9" fill={emerald} />
      <path d="M8 10L12 22L16 12L20 22L24 10" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="23" cy="9.5" r="3" fill="#F59E0B" />
    </svg>
  );
}
